import React, { useState } from 'react';
import { ShareNetwork, Link as LinkIcon, Lock, Clock, Copy, X } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;

const ShareReportModal = ({ reportId, onClose }) => {
  const [expiration, setExpiration] = useState('30d');
  const [password, setPassword] = useState('');
  const [usePassword, setUsePassword] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareResult, setShareResult] = useState(null);

  const handleShare = async () => {
    setSharing(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/reports/share`,
        {
          report_id: reportId,
          expiration,
          password: usePassword ? password : null,
        },
        { withCredentials: true }
      );
      const fullUrl = `${window.location.origin}${data.share_url}`;
      setShareResult({ ...data, full_url: fullUrl });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create share link');
    } finally {
      setSharing(false);
    }
  };

  const copyLink = () => {
    if (shareResult?.full_url) {
      navigator.clipboard.writeText(shareResult.full_url);
      toast.success('Link copied to clipboard');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="share-modal-overlay">
      <div className="bg-white w-full max-w-md" data-testid="share-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <ShareNetwork size={20} weight="bold" className="text-blue-600" />
            <h2 className="text-base font-semibold text-slate-950">Share Report</h2>
          </div>
          <button onClick={onClose} data-testid="share-modal-close" className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={20} weight="bold" />
          </button>
        </div>

        {!shareResult ? (
          <div className="p-5 space-y-5">
            {/* Expiration */}
            <div>
              <label className="block text-xs font-medium uppercase tracking-[0.15em] text-slate-500 mb-2">
                <Clock size={12} className="inline mr-1" />
                Link Expires In
              </label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { val: '7d', label: '7 Days' },
                  { val: '30d', label: '30 Days' },
                  { val: 'never', label: 'Never' },
                ].map(({ val, label }) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setExpiration(val)}
                    data-testid={`expire-${val}`}
                    className={`py-2 text-xs font-medium border transition-all ${
                      expiration === val
                        ? 'border-slate-950 bg-slate-950 text-white'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="flex items-center gap-2 cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={usePassword}
                  onChange={(e) => setUsePassword(e.target.checked)}
                  data-testid="password-toggle"
                  className="w-4 h-4 border-slate-300 rounded text-blue-600 focus:ring-blue-600"
                />
                <span className="text-xs font-medium uppercase tracking-[0.15em] text-slate-500">
                  <Lock size={12} className="inline mr-1" />
                  Password Protection
                </span>
              </label>
              {usePassword && (
                <input
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter a password"
                  data-testid="share-password-input"
                  className="w-full px-3 py-2 border border-slate-300 text-sm focus:ring-2 focus:ring-blue-600 focus:ring-offset-1 focus:outline-none"
                />
              )}
            </div>

            <button
              onClick={handleShare}
              disabled={sharing || (usePassword && !password.trim())}
              data-testid="create-share-link-btn"
              className="w-full py-2.5 bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-300 text-sm font-medium transition-colors flex items-center justify-center gap-2"
            >
              <LinkIcon size={16} weight="bold" />
              {sharing ? 'Creating...' : 'Create Share Link'}
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-4" data-testid="share-result">
            <div className="p-3 bg-emerald-50 border border-emerald-200 text-center">
              <p className="text-sm text-emerald-700 font-medium">Share link created</p>
            </div>

            {/* Copy link */}
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={shareResult.full_url}
                data-testid="share-url-input"
                className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 text-xs font-mono text-slate-700 truncate"
              />
              <button
                onClick={copyLink}
                data-testid="copy-share-link-btn"
                className="px-3 py-2 bg-slate-950 text-white hover:bg-slate-800 transition-colors"
              >
                <Copy size={16} weight="bold" />
              </button>
            </div>

            {/* Details */}
            <div className="text-xs text-slate-500 space-y-1">
              <p>Expires: <span className="font-medium text-slate-700">{shareResult.expiry_label}</span></p>
              {shareResult.password_protected && (
                <p className="flex items-center gap-1">
                  <Lock size={12} /> Password protected
                </p>
              )}
            </div>

            <button
              onClick={onClose}
              className="w-full py-2.5 border border-slate-200 text-slate-700 hover:bg-slate-50 text-sm font-medium transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShareReportModal;
